import { fetchAuthSession } from 'aws-amplify/auth';

export interface Organisation {
  readonly id: string;
  readonly name: string;
  readonly role: string;
}

export class ApiError extends Error {}

async function cognitoHeaders(): Promise<Record<string, string>> {
  const token = (await fetchAuthSession()).tokens?.idToken?.toString();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Requests in flight longer than SLOW_MS. The database sleeps when idle (ADR-010) and the first
 * request after a pause can take up to 30 seconds; the app says so instead of looking frozen.
 */
const SLOW_MS = 3000;
let slowRequests = 0;
const slowListeners = new Set<(slow: boolean) => void>();

export function onSlowRequests(listener: (slow: boolean) => void): () => void {
  slowListeners.add(listener);
  return () => slowListeners.delete(listener);
}

function trackSlow<T>(promise: Promise<T>): Promise<T> {
  let counted = false;
  const timer = setTimeout(() => {
    counted = true;
    if (++slowRequests === 1) for (const l of slowListeners) l(true);
  }, SLOW_MS);
  return promise.finally(() => {
    clearTimeout(timer);
    if (counted && --slowRequests === 0) for (const l of slowListeners) l(false);
  });
}

export function createApi(baseUrl: string, devUser?: string) {
  function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    return trackSlow(send<T>(path, init));
  }
  async function send<T>(path: string, init: RequestInit): Promise<T> {
    const headers = new Headers(init.headers);
    const auth = devUser ? { 'X-Dev-User': devUser } : await cognitoHeaders();
    for (const [k, v] of Object.entries(auth)) headers.set(k, v);
    if (init.body) headers.set('Content-Type', 'application/json');
    const res = await fetch(`${baseUrl}${path}`, { ...init, headers });
    const body = (await res.json().catch(() => ({}))) as { message?: string; problems?: string[] };
    if (!res.ok) {
      throw new ApiError(
        body.message ?? body.problems?.join(' ') ?? 'Something went wrong. Please try again.',
      );
    }
    return body as T;
  }
  return {
    me: () => request<{ organisations: Organisation[]; operator?: boolean }>('/me'),
    /** A signed-in request outside an organisation (ABN check while setting up). */
    raw: <T>(path: string) => request<T>(path),
    createOrganisation: (name: string) =>
      request<{ id: string }>('/organisations', { method: 'POST', body: JSON.stringify({ name }) }),
    renameBusiness: (orgId: string, name: string) =>
      request<{ name: string }>(`/orgs/${orgId}/business`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      }),
    get: <T>(orgId: string, path: string) => request<T>(`/orgs/${orgId}${path}`),
    /** Downloads a report file (CSV or XLSX) and hands it to the browser. */
    download: async (orgId: string, path: string, fileName: string) => {
      const headers = new Headers(devUser ? { 'X-Dev-User': devUser } : await cognitoHeaders());
      const res = await trackSlow(fetch(`${baseUrl}/orgs/${orgId}${path}`, { headers }));
      if (!res.ok) throw new ApiError('The file could not be downloaded. Try again.');
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    },
    send: <T>(orgId: string, method: 'POST' | 'PUT' | 'PATCH', path: string, body: unknown = {}) =>
      request<T>(`/orgs/${orgId}${path}`, { method, body: JSON.stringify(body) }),
  };
}

export type Api = ReturnType<typeof createApi>;
